// MAPS.TEARDOWN_AND_OFFLINE B: the one answer to "can this tab reach Trayvar?".
//
// Before this, five places each guessed: Live's pill read only its realtime channel
// (which can stay "joined" long after the network goes - DevTools Offline does not
// close a WebSocket at all), createGuardedRefresh and useRetryWhenOnline read
// navigator.onLine and waited silently, VersionWatcher skipped its poll, and
// isTransportFailure read navigator.onLine for a bare TypeError. Nothing listened for
// the browser's `offline` event, so on staging (1 Nov 12:41) Live went offline and
// still said "Live - updated 12:41", with no notice anywhere.
//
// Three states:
//   online       the browser is online and the last reachability probe (if any) answered;
//   offline      the browser says it is offline (navigator.onLine false / `offline`);
//   unreachable  the browser says online, but a probe of /api/build-id failed.
// The browser's events are authoritative for offline/online; a probe can only move
// online <-> unreachable. Framework-free, so the plain modules (reportClientError,
// guardedRefresh) and the components read the same state.

export type Connectivity = "online" | "offline" | "unreachable";
export type ConnectivityEvent = "browser-online" | "browser-offline" | "probe-ok" | "probe-failed";

/** The state machine, pure for the smoke. */
export function nextConnectivity(state: Connectivity, event: ConnectivityEvent): Connectivity {
  switch (event) {
    case "browser-offline":
      return "offline";
    case "browser-online":
      return "online";
    case "probe-ok":
      return state === "offline" ? "offline" : "online";
    case "probe-failed":
      return state === "offline" ? "offline" : "unreachable";
  }
}

type Listener = (state: Connectivity, prev: Connectivity) => void;

let state: Connectivity = "online";
let listening = false;
const listeners = new Set<Listener>();

function apply(event: ConnectivityEvent): void {
  const prev = state;
  const next = nextConnectivity(state, event);
  if (next === prev) return;
  state = next;
  for (const l of [...listeners]) l(next, prev);
}

function ensureListening(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) state = "offline";
  window.addEventListener("offline", () => apply("browser-offline"));
  window.addEventListener("online", () => apply("browser-online"));
}

/** The current state. */
export function getConnectivity(): Connectivity {
  ensureListening();
  return state;
}

/** True unless the browser says it is offline (an unreachable server is still "online"
 *  to the browser - that case is the probe's to decide). */
export function browserOnline(): boolean {
  return getConnectivity() !== "offline";
}

/** Called on every change with (next, prev). Returns the unsubscribe. */
export function subscribeConnectivity(listener: Listener): () => void {
  ensureListening();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Run `cb` once, the next time the browser comes back from offline. Returns a cancel. */
export function onceBackOnline(cb: () => void): () => void {
  const off = subscribeConnectivity((next, prev) => {
    if (prev === "offline" && next !== "offline") {
      off();
      cb();
    }
  });
  return off;
}

/** The outcome of a reachability probe (createGuardedRefresh's GET of /api/build-id). */
export function reportReachability(ok: boolean): void {
  ensureListening();
  apply(ok ? "probe-ok" : "probe-failed");
}

/** Test-only: drive the browser's events and reset between cases. */
export function __setBrowserOnline(online: boolean): void {
  apply(online ? "browser-online" : "browser-offline");
}
export function __resetConnectivity(): void {
  state = "online";
  listeners.clear();
}
